import { DateTime } from 'luxon';
import { HeaderData } from '../header.class';
import { OptionPersistence } from '../option/option.class';
import { TimePeriod, TimePeriodData } from '../time-period.class';
import { ChoiceData, ChoiceEnum } from '../vote/choice.class';
import { VoteData } from '../vote/vote.class';
import { TemplateSettingFlags, hasTimePeriod, headerLocation, isBlank, isHeaderWhenVisible, optionShape, toArray, toChoiceData } from '../persistence.utils';
import { headerMeetingLink, meetingLinkVisibility } from '../meeting-link.utils';

/**
 * The one source of truth for the calendar export (workorder-calendar-quick-wins-a-2026-11-02, task 1a):
 * which dates of a poll go into a calendar and with which data. The client download, the calendar links
 * and later the attachment of the vote confirmation mail (package B, server side) all use this function,
 * so it imports nothing from Angular, Firebase or the browser.
 *
 * Visibility of header date, location and meeting link is the one of the vote page and the mail renderer
 * (functions/src/whocan/mail/vote-renderer.ts), all read it from persistence.utils / meeting-link.utils
 * (isHeaderWhenVisible, headerLocation, headerMeetingLink): the date only with showWhen/showWhenAdvanced and a
 * stored date, location and meeting link with a text when the poll template has no toggle for them, otherwise
 * only with showLocationAdvanced / showMeetingLinkAdvanced (calendar quick wins C, task 0); the meeting link only in
 * templates that offer it (C2). Options in sortMap order,
 * deleted options and dividers skipped. Times are wall time as entered - never converted through the
 * zone of the machine; the events carry header.timeZone, without it they are floating.
 */

/**
 * all: the header date and every date option (owner, context action bar)
 * votedFor: what the voter said yes or maybe to - the header date only when he accepted the invitation
 */
export type CalendarScope =
    | { kind: 'all' }
    | { kind: 'votedFor'; vote: VoteData | null | undefined };

export interface CalendarEvent {
    /** stable per poll and date, so a second import replaces the entry instead of doubling it */
    uid: string;
    /** wall time "2026-11-14T19:00:00", date "2026-11-14" for all day */
    start: string;
    /** always set: exclusive for all day (+1 day), start + 1 h for open end */
    end: string;
    allDay: boolean;
    /** header.timeZone, trimmed; undefined = floating */
    timeZone?: string;
    /** pollTitle, "pollTitle – label" for a date option with a label; the maybe suffix is added by the serializers */
    summary: string;
    location?: string;
    /** the meeting link (package C), normalized https url - ICS URL, the location when there is none, first line of the description */
    url?: string;
    /** the voter answered maybe */
    tentative: boolean;
    source: { kind: 'when' } | { kind: 'option'; index: number };
}

export interface CalendarEventsInput {
    pollId: string;
    header: HeaderData;
    /** whoo/options/{o}/{p}/optionArray - array or firebase map form */
    options: OptionPersistence[] | Record<string, OptionPersistence> | null | undefined;
    /** whoo/options/{o}/{p}/sortMap - display order, option indexes of the not deleted options */
    sortMap?: number[] | Record<string, number> | null;
    scope: CalendarScope;
    /** availableSettings of the poll template (locationAdvanced, meetingLinkAdvanced, meetingLinkAvailable) - decide when location and meeting link show */
    templateFlags: TemplateSettingFlags;
}

const UID_DOMAIN = 'whocan.org';
const EN_DASH = '–';
const WALL_TIME_FORMAT = "yyyy-LL-dd'T'HH:mm:ss";

export function calendarEventsFor(input: CalendarEventsInput): CalendarEvent[] {
    const { pollId, header, scope } = input;
    if (!header) return [];
    const vote = scope.kind === 'votedFor' ? scope.vote : undefined;
    if (scope.kind === 'votedFor' && !vote) return [];

    const pollTitle = isBlank(header.pollTitle) ? '' : String(header.pollTitle).trim();
    const timeZone = isBlank(header.timeZone) ? undefined : String(header.timeZone).trim();
    const location = headerLocation(header, { hasAdvancedToggle: input.templateFlags.locationAdvanced });
    const url = headerMeetingLink(header, meetingLinkVisibility(input.templateFlags));
    const events: CalendarEvent[] = [];

    /* header date */
    const whenVisible = isHeaderWhenVisible(header);
    const whenWanted = scope.kind === 'all' || vote.choiceInvitation === 1;
    if (whenVisible && whenWanted) {
        const times = toEventTimes(header.when);
        if (times) {
            events.push({
                uid: `${pollId}-when@${UID_DOMAIN}`,
                ...times,
                ...(timeZone ? { timeZone } : {}),
                summary: pollTitle,
                ...(location ? { location } : {}),
                ...(url ? { url } : {}),
                tentative: false,
                source: { kind: 'when' },
            });
        }
    }
    /* date options */
    const optionArray = toArray(input.options);
    const choices = toArray(vote?.choicesArray);
    const sortMap = toArray(input.sortMap).filter((index): index is number => Number.isInteger(index));
    const order: number[] = sortMap.length > 0 ? sortMap : optionArray.map((_, index) => index);
    for (const position of order) {
        const option = optionArray[position];
        if (!option || option.isDeleted) continue;
        if (option.optionConfigKey === 'divider') continue;
        if (!optionShape(option.optionConfigKey, option.type as string | undefined).isDate) continue;
        if (!hasTimePeriod(option.timePeriod)) continue;
        const optionIndex = typeof option.index === 'number' ? option.index : position;

        let tentative = false;
        if (scope.kind === 'votedFor') {
            const choiceEnum = toChoiceData(choices[optionIndex] as ChoiceEnum | ChoiceData | undefined).choiceEnum;
            if (choiceEnum === ChoiceEnum.mayBe) tentative = true;
            else if (choiceEnum !== ChoiceEnum.yes) continue;
        }
        const times = toEventTimes(option.timePeriod);
        if (!times) continue;
        const label = isBlank(option.label) ? '' : String(option.label).trim();
        events.push({
            uid: `${pollId}-opt-${optionIndex}@${UID_DOMAIN}`,
            ...times,
            ...(timeZone ? { timeZone } : {}),
            summary: [pollTitle, label].filter(part => part !== '').join(` ${EN_DASH} `),
            ...(location ? { location } : {}),
            ...(url ? { url } : {}),
            tentative,
            source: { kind: 'option', index: optionIndex },
        });
    }
    return events;
}

/**
 * Start and end of one event as wall time strings. Through TimePeriod, so the legacy date/startHour form is
 * covered too; null when the stored period is unusable.
 * - all day: end exclusive - the day after the last day (multi day) or after the start day
 * - open end: start + 1 hour (decision K7), across midnight too
 * - with time: as stored; an end that is not after the start counts as open end (no events of duration 0)
 */
function toEventTimes(data: TimePeriodData): Pick<CalendarEvent, 'start' | 'end' | 'allDay'> | null {
    let period: TimePeriod;
    try {
        period = TimePeriod.newFromTimePeriodData(data);
    } catch (error) {
        return null;
    }
    // the wall time string has no zone; parsing it in UTC keeps it exactly as written
    const start = DateTime.fromISO(period.startString ?? '', { zone: 'utc' });
    if (!start.isValid) return null;
    const storedEnd = DateTime.fromISO(period.endString ?? '', { zone: 'utc' });

    if (period.isAllDay) {
        const startDay = start.startOf('day');
        const lastDay = period.isMultiDay && storedEnd.isValid && storedEnd.startOf('day') > startDay
            ? storedEnd.startOf('day')
            : startDay;
        return { start: startDay.toISODate(), end: lastDay.plus({ days: 1 }).toISODate(), allDay: true };
    }

    let end: DateTime | null = null;
    if (!period.isOpenEnd && storedEnd.isValid) {
        end = period.isMultiDay
            ? storedEnd
            : start.set({ hour: storedEnd.hour, minute: storedEnd.minute, second: storedEnd.second });
    }
    if (!end || end <= start) end = start.plus({ hours: 1 });
    return { start: start.toFormat(WALL_TIME_FORMAT), end: end.toFormat(WALL_TIME_FORMAT), allDay: false };
}
